/**
 * Multi-tenant site detection.
 *
 * Three mechanisms, in priority order:
 *   1. REACT_APP_SITE_LOCK env var  (used by single-tenant forked apps
 *      e.g. the standalone Clinic deployment on book.whitesmileclinic.ie)
 *   2. ?site=clinic query param     (used on the preview URL + admin toggle)
 *   3. hostname contains "whitesmileclinic"  (used in production)
 *
 * The chosen site is cached in sessionStorage so it survives client-side
 * navigations (react-router pushes) without ever leaking across browser
 * tabs — each preview tab keeps its own site.
 */

const STORAGE_KEY = "ws_site";
const VALID = new Set(["dental", "clinic"]);

/** Read the site from URL or hostname, cache it, and return it. */
export function getSite() {
  if (typeof window === "undefined") return "dental";

  // 0. Deployment-level lock (highest priority). When present, this app
  // is a single-tenant fork and MUST always be one site — no query
  // param, no hostname trickery, nothing can flip it. Left unset on the
  // multi-tenant dental app so the historic behaviour is preserved.
  const lock = (process.env.REACT_APP_SITE_LOCK || "").toLowerCase();
  if (VALID.has(lock)) {
    sessionStorage.setItem(STORAGE_KEY, lock);
    return lock;
  }

  const url = new URL(window.location.href);

  // 1. Explicit ?site= param always wins. Used by admin toggle + preview.
  const explicit = url.searchParams.get("site");
  if (explicit && VALID.has(explicit)) {
    sessionStorage.setItem(STORAGE_KEY, explicit);
    return explicit;
  }

  // 2. Hostname is AUTHORITATIVE for production domains. A visitor on
  // book.whitesmiledental.ie must ALWAYS see the dental experience,
  // regardless of any stale sessionStorage from an earlier ?site=clinic
  // visit in the same private/incognito tab (the exact bug that caused
  // dental customers to see the clinic booking system in incognito Chrome).
  const host = (url.hostname || "").toLowerCase();
  if (host.includes("whitesmileclinic")) {
    sessionStorage.setItem(STORAGE_KEY, "clinic");
    return "clinic";
  }
  if (host.includes("whitesmiledental")) {
    sessionStorage.setItem(STORAGE_KEY, "dental");
    return "dental";
  }

  // 3. On preview / localhost (neither production hostname matches), rely
  // on sessionStorage so an admin who navigated in with ?site=clinic keeps
  // that context across client-side route changes.
  const cached = sessionStorage.getItem(STORAGE_KEY);
  if (cached && VALID.has(cached)) return cached;

  sessionStorage.setItem(STORAGE_KEY, "dental");
  return "dental";
}

/** True when the current tab is showing the White Smile Clinic experience. */
export function isClinic() {
  return getSite() === "clinic";
}

/** Human-readable brand for the current site. */
export function brandName() {
  return isClinic() ? "White Smile Clinic" : "White Smile Dental";
}

/** Explicitly set the site (used by the admin's Dental/Clinic toggle). */
export function setSite(site) {
  if (!VALID.has(site)) return;
  sessionStorage.setItem(STORAGE_KEY, site);
}
